import type { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SvgCart } from '@/components/icons-eataly';
import {
    commerceNotificationCardClassName,
    commerceNotificationDesktopWidthClassName,
    commerceNotificationDismissLayoutClassName,
    commerceNotificationMessageClassName,
} from '@/components/toast/commerce-notification-styles';
import { DismissCloseButton } from '@/components/ui/dismiss-close-button';
import type { WishlistBulkAddSummary } from '@/lib/api/wishlist.server';
import { cn } from '@/lib/utils';

export type CommerceActionNotificationVariant = 'desktop' | 'mobile-drawer';

export type CommerceActionNotificationProps = {
    variant: CommerceActionNotificationVariant;
    productName: string;
    listNames: string[];
    removedListNames?: string[];
    alreadyInList?: boolean;
    bulkAddSummary?: WishlistBulkAddSummary;
    showCartIcon?: boolean;
    onDismiss?: () => void;
    className?: string;
};

function ListNamesBold({ names, conjunction }: { names: string[]; conjunction: string }) {
    if (names.length === 0) {
        return null;
    }
    if (names.length === 1) {
        return <strong className="font-bold">{names[0]}</strong>;
    }
    if (names.length === 2) {
        return (
            <>
                <strong className="font-bold">{names[0]}</strong>
                {` ${conjunction} `}
                <strong className="font-bold">{names[1]}</strong>
            </>
        );
    }
    return (
        <>
            {names.map((name, index) => (
                <span key={name}>
                    {index > 0 ? ', ' : null}
                    <strong className="font-bold">{name}</strong>
                </span>
            ))}
        </>
    );
}

function WishlistAddedNotificationParagraph({
    productName,
    listNames,
    alreadyInList,
    conjunction,
}: {
    productName: string;
    listNames: string[];
    alreadyInList: boolean;
    conjunction: string;
}) {
    const { t } = useTranslation('product');
    const count = listNames.length;
    const middleKey = alreadyInList
        ? count === 1
            ? 'wishlistNotification.alreadyMiddle_one'
            : 'wishlistNotification.alreadyMiddle_other'
        : count === 1
          ? 'wishlistNotification.savedMiddle_one'
          : 'wishlistNotification.savedMiddle_other';

    return (
        <p className={commerceNotificationMessageClassName}>
            <strong className="font-bold">{productName}</strong>
            {t(middleKey)}
            <ListNamesBold names={listNames} conjunction={conjunction} />
        </p>
    );
}

function WishlistRemovedNotificationParagraph({
    productName,
    removedListNames,
    conjunction,
}: {
    productName: string;
    removedListNames: string[];
    conjunction: string;
}) {
    const { t } = useTranslation('product');
    const count = removedListNames.length;
    const middleKey =
        count === 1 ? 'wishlistNotification.removedMiddle_one' : 'wishlistNotification.removedMiddle_other';

    return (
        <p className={commerceNotificationMessageClassName}>
            <strong className="font-bold">{productName}</strong>
            {t(middleKey)}
            <ListNamesBold names={removedListNames} conjunction={conjunction} />
        </p>
    );
}

function BulkWishlistSavedNotificationParagraph({
    listName,
    bulkAddSummary,
}: {
    listName: string;
    bulkAddSummary: WishlistBulkAddSummary;
}) {
    const messageKey =
        bulkAddSummary.addedCount === 0
            ? 'confirmation.bulkWishlist.savedNone'
            : bulkAddSummary.skippedCount > 0
              ? 'confirmation.bulkWishlist.savedPartial'
              : 'confirmation.bulkWishlist.savedSuccess';

    return (
        <p className={commerceNotificationMessageClassName}>
            <Trans
                i18nKey={messageKey}
                ns="checkout"
                count={bulkAddSummary.addedCount}
                values={{
                    count: bulkAddSummary.addedCount,
                    listName,
                }}
                components={{
                    list: <strong className="font-bold" />,
                }}
            />
        </p>
    );
}

export function CommerceActionNotificationMessage({
    productName,
    listNames,
    removedListNames = [],
    alreadyInList = false,
    bulkAddSummary,
}: Pick<
    CommerceActionNotificationProps,
    'productName' | 'listNames' | 'removedListNames' | 'alreadyInList' | 'bulkAddSummary'
>) {
    const { t } = useTranslation('product');

    if (bulkAddSummary && listNames.length > 0) {
        return <BulkWishlistSavedNotificationParagraph listName={listNames[0] ?? ''} bulkAddSummary={bulkAddSummary} />;
    }

    const conjunction = t('wishlistNotification.listConjunction');
    const addedParagraph =
        listNames.length > 0 ? (
            <WishlistAddedNotificationParagraph
                productName={productName}
                listNames={listNames}
                alreadyInList={alreadyInList}
                conjunction={conjunction}
            />
        ) : null;
    const removedParagraph =
        removedListNames.length > 0 ? (
            <WishlistRemovedNotificationParagraph
                productName={productName}
                removedListNames={removedListNames}
                conjunction={conjunction}
            />
        ) : null;

    if (addedParagraph && removedParagraph) {
        return (
            <div className="flex flex-col gap-2">
                {addedParagraph}
                {removedParagraph}
            </div>
        );
    }

    return addedParagraph ?? removedParagraph;
}

function CommerceNotificationDismissButton({
    onDismiss,
    closeLabel,
    variant,
}: {
    onDismiss: () => void;
    closeLabel: string;
    variant: CommerceActionNotificationVariant;
}) {
    return (
        <DismissCloseButton
            aria-label={closeLabel}
            onClick={onDismiss}
            className={variant === 'desktop' ? commerceNotificationDismissLayoutClassName : undefined}
        />
    );
}

export function CommerceActionNotification({
    variant,
    productName,
    listNames,
    removedListNames = [],
    alreadyInList = false,
    bulkAddSummary,
    showCartIcon = false,
    onDismiss,
    className,
}: CommerceActionNotificationProps): ReactElement {
    const { t } = useTranslation('product');
    const isDesktop = variant === 'desktop';
    const closeLabel = t('wishlistSheet.close');

    if (!isDesktop) {
        return (
            <div role="status" aria-live="polite" className={cn('flex gap-4 justify-between')}>
                <div className={cn('flex pb-6 min-w-0 items-start', showCartIcon && 'gap-6')}>
                    {showCartIcon ? <SvgCart className="size-6 shrink-0 text-foreground" aria-hidden /> : null}
                    <CommerceActionNotificationMessage
                        productName={productName}
                        listNames={listNames}
                        removedListNames={removedListNames}
                        alreadyInList={alreadyInList}
                        bulkAddSummary={bulkAddSummary}
                    />
                </div>
                {onDismiss ? (
                    <CommerceNotificationDismissButton
                        onDismiss={onDismiss}
                        closeLabel={closeLabel}
                        variant={variant}
                    />
                ) : null}
            </div>
        );
    }

    return (
        <div
            role="status"
            aria-live="polite"
            className={cn(
                commerceNotificationCardClassName,
                isDesktop ? commerceNotificationDesktopWidthClassName : 'w-full max-w-none pb-20',
                className
            )}>
            <div className={cn('flex min-w-0 items-start', showCartIcon && 'gap-4')}>
                {showCartIcon ? <SvgCart className="size-6 shrink-0 text-foreground" aria-hidden /> : null}
                <CommerceActionNotificationMessage
                    productName={productName}
                    listNames={listNames}
                    removedListNames={removedListNames}
                    alreadyInList={alreadyInList}
                    bulkAddSummary={bulkAddSummary}
                />
            </div>
            {onDismiss ? (
                <CommerceNotificationDismissButton onDismiss={onDismiss} closeLabel={closeLabel} variant={variant} />
            ) : null}
        </div>
    );
}
